type Translate = (key: string) => string;

const replacePlaceholder = (value: string, placeholder: string, replacement: string): string =>
  value.replace(placeholder, replacement);

const translateDetail = (detail: string, t: Translate): string => {
  const dynamicMessages: Array<{ pattern: RegExp; key: string }> = [
    { pattern: /^Customer '(.+)' already exists$/, key: 'bulkUpload.customerAlreadyExists' },
    { pattern: /^Item '(.+)' already exists$/, key: 'bulkUpload.itemAlreadyExists' },
    { pattern: /^Item number '(.+)' already exists$/, key: 'bulkUpload.itemNumberAlreadyExists' },
    { pattern: /^Barcode '(.+)' already exists$/, key: 'bulkUpload.barcodeAlreadyExists' },
  ];

  for (const { pattern, key } of dynamicMessages) {
    const match = detail.match(pattern);
    if (match) {
      return replacePlaceholder(t(key), '{{value}}', match[1]);
    }
  }

  const messages: Record<string, string> = {
    'First name is required': 'bulkUpload.firstNameRequired',
    'Last name is required': 'bulkUpload.lastNameRequired',
    'Invalid email format': 'bulkUpload.invalidEmail',
    'Tax identification number must not exceed 32 characters': 'bulkUpload.taxIdTooLong',
    'Item name is required': 'bulkUpload.itemNameRequired',
    'Price is required': 'bulkUpload.priceRequired',
    'Price must be non-negative': 'bulkUpload.priceNonNegative',
    'Price must be a valid number': 'bulkUpload.priceInvalid',
    'VAT rate must be one of 0, 1, 10, or 20': 'bulkUpload.vatRateInvalid',
    'VAT rate must be a valid number': 'bulkUpload.vatRateNumberInvalid',
    'Code contains invalid characters': 'bulkUpload.codeInvalid',
    'File is empty': 'bulkUpload.fileEmpty',
    'File must be a CSV file': 'bulkUpload.fileMustBeCsv',
    'Error processing CSV file': 'bulkUpload.processingError',
    'Company not found': 'bulkUpload.companyNotFound',
    'Super admin must specify company ID': 'bulkUpload.companyRequired',
    'User must be associated with a company': 'bulkUpload.userCompanyRequired',
    'Customer email already exists in this company!': 'bulkUpload.customerEmailAlreadyExists',
    'Customer email already exists in this company': 'bulkUpload.customerEmailAlreadyExists',
    'Customer tax identification number already exists in this company!': 'bulkUpload.customerTaxIdentificationNumberAlreadyExists',
    'Customer tax identification number already exists in this company': 'bulkUpload.customerTaxIdentificationNumberAlreadyExists',
    'A customer with this name already exists; provide a phone number, email, or tax identification number to distinguish the customer': 'bulkUpload.duplicateNameRequiresIdentifier',
    'Customer already exists in this company': 'bulkUpload.customerAlreadyExistsInCompany',
    'Could not process this customer row': 'bulkUpload.customerRowProcessingError',
    "CSV must contain 'firstName' and 'lastName' columns": 'bulkUpload.customerHeadersRequired',
    "CSV must contain 'name' and 'price' columns. Optional: 'itemNumber', 'barcode', 'vatRate', 'categories'": 'bulkUpload.itemHeadersRequired',
  };

  const key = messages[detail];
  return key ? t(key) : detail;
};

/**
 * Converts the backend's row-prefixed CSV errors into the active UI language.
 * Unknown messages are kept intact so a useful server-side diagnostic is not lost.
 */
export const formatBulkUploadError = (rawError: string, t: Translate): string => {
  const rowMatch = rawError.match(/^Row\s+(\d+):\s*(.*)$/i);
  if (!rowMatch) {
    return translateDetail(rawError, t);
  }

  const rowNumber = rowMatch[1];
  const detail = translateDetail(rowMatch[2], t);
  return t('bulkUpload.rowError')
    .replace('{0}', rowNumber)
    .replace('{1}', detail);
};
